import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaSearch } from 'react-icons/fa';
import { getRejectedCustomers } from '../../../models/customersModel';

function RejectedCustomers() {
  const [rejectedCustomers, setRejectedCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchRejectedCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        pageSize: pageSize
      };
      
      if (searchTerm) params.filter = searchTerm;
      
      const response = await getRejectedCustomers(params);
      if (response.status === 1) {
        // Handle both old format (rows) and new format (data)
        const responseData = response.data.data || response.data.rows || [];
        const meta = response.data.meta || {};
        
        setRejectedCustomers(responseData);
        setTotalCount(meta.total || responseData.length);
      }
    } catch (error) {
      console.error('Error fetching rejected customers:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    fetchRejectedCustomers();
  }, [fetchRejectedCustomers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  if (loading && rejectedCustomers.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 py-1">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start">
            <Link
              to="/customers"
              className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5 text-gray-600 hover:text-gray-800"
              style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}
            >
              <FaArrowLeft className="h-5 w-5" style={{ color: '#0096DE' }} />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Rejected Customer Details</h1>
              <p className="text-xs text-gray-500 mb-1">Customer Account Rejections • Total: <span className="font-semibold text-red-600">{totalCount}</span></p>
              <div className="flex items-center mt-1">
                <span className="text-xs font-medium" style={{ color: '#0096DE' }}>
                  {totalCount ? `Showing ${Math.min(pageSize, totalCount)} of ${totalCount} results` : '0 results'}
                  {searchTerm && (
                    <span className="ml-1 text-blue-600">(filtered)</span>
                  )}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col gap-2 items-end min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="inline-flex items-center px-2 py-1.5 border border-red-300 rounded-md shadow-sm text-xs text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-1 focus:ring-red-500 flex-shrink-0"
                >
                  Clear Filters
                </button>
              )}
              
              <button 
                onClick={() => fetchRejectedCustomers()} 
                className="inline-flex items-center px-2 py-1.5 border border-gray-300 shadow-sm text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0"
                title="Refresh"
                disabled={loading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke={loading ? '#0096DE' : 'currentColor'}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>

              <div className="flex-shrink-0">
                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="text-sm px-2 py-1.5 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[10, 20, 30, 50].map(size => (
                    <option key={size} value={size}>{size} per page</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search rejected customers..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Account</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Name</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Email</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Mobile</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Rejection Date</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td colSpan="6" className="px-3 py-4 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm">Loading...</span>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && rejectedCustomers.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-3 py-6 text-center">
                    <p className="text-gray-600">No rejected customers found</p>
                  </td>
                </tr>
              )}
              {!loading && rejectedCustomers.map((customer, index) => (
                <tr key={customer.id || index} className="hover:bg-blue-50">
                  <td className="px-3 py-2 text-sm font-medium text-gray-900">
                    {customer.account}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    {customer.account_name || '-'}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    {customer.account_email || '-'}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    {customer.account_mobile || '-'}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    {customer.rejected_date ? new Date(customer.rejected_date).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-red-100 text-red-800">
                      Rejected
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalCount > 0 && (
          <div className="bg-gray-50 px-3 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-4">
              <div className="text-gray-500">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Show:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span className="text-gray-500">per page</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Previous
              </button>
              
              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {(() => {
                  const pageNumbers = [];
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);
                  
                  if (endPage - startPage < 4) {
                    startPage = Math.max(1, endPage - 4);
                  }
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1.5 border rounded text-xs font-medium ${
                          i === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  return pageNumbers;
                })()}
              </div>
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RejectedCustomers;
